import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void> | void;
  pullDistance?: number;
  triggerDistance?: number;
  resistance?: number;
  enabled?: boolean;
  className?: string;
}

export function PullToRefresh({
  children,
  onRefresh,
  pullDistance = 80,
  triggerDistance = 60,
  resistance = 0.6,
  enabled = true,
  className
}: PullToRefreshProps) {
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullOffset, setPullOffset] = useState(0);
  const [canRefresh, setCanRefresh] = useState(false);

  const startY = useRef(0);
  const currentY = useRef(0);
  const isAtTop = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (isRefreshing) return;
      
      // Check if we're at the top of the scrollable area
      isAtTop.current = container.scrollTop === 0;
      
      if (isAtTop.current) {
        startY.current = e.touches[0].clientY;
        setIsPulling(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling || isRefreshing || !isAtTop.current) return;

      currentY.current = e.touches[0].clientY;
      const diff = currentY.current - startY.current;

      if (diff > 0) {
        // Apply resistance to the pull
        const offset = Math.min(diff * resistance, pullDistance);
        setPullOffset(offset);
        setCanRefresh(offset >= triggerDistance);

        // Prevent default scrolling when pulling down
        e.preventDefault();
      }
    };

    const handleTouchEnd = async () => {
      if (!isPulling || isRefreshing) return;

      setIsPulling(false);

      if (canRefresh) {
        setIsRefreshing(true);
        setPullOffset(triggerDistance); // Keep at trigger position during refresh
        
        try {
          await onRefresh();
        } catch (error) {
          console.error('Refresh failed:', error);
        } finally {
          setIsRefreshing(false);
          setPullOffset(0);
          setCanRefresh(false);
        }
      } else {
        // Animate back to 0
        setPullOffset(0);
        setCanRefresh(false);
      }
    };

    // Add event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, isPulling, isRefreshing, canRefresh, onRefresh, pullDistance, triggerDistance, resistance]);

  const refreshIndicatorOpacity = Math.min(pullOffset / triggerDistance, 1);
  const refreshIndicatorScale = Math.min(0.8 + (pullOffset / triggerDistance) * 0.2, 1);

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-auto', className)}
      style={{
        transform: `translateY(${pullOffset}px)`,
        transition: isPulling || isRefreshing ? 'none' : 'transform 0.3s ease-out'
      }}
    >
      {/* Refresh Indicator */}
      <div
        className="absolute top-0 left-0 w-full flex justify-center items-center z-10 pointer-events-none"
        style={{
          height: triggerDistance,
          transform: `translateY(-${triggerDistance - pullOffset}px)`,
          opacity: refreshIndicatorOpacity
        }}
      >
        <div
          className={cn(
            'w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg',
            canRefresh && 'bg-green-500',
            isRefreshing && 'bg-blue-500'
          )}
          style={{
            transform: `scale(${refreshIndicatorScale})`,
            transition: isPulling ? 'none' : 'all 0.3s ease-out'
          }}
        >
          <RefreshCw 
            className={cn(
              'w-4 h-4',
              isRefreshing && 'animate-spin'
            )} 
          />
        </div>
      </div>

      {/* Content */}
      {children}
    </div>
  );
}

// Hook for manual refresh trigger
export function useRefresh() {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refresh = async (refreshFunction: () => Promise<void> | void) => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      await refreshFunction();
    } catch (error) {
      console.error('Manual refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return { isRefreshing, refresh };
}